/**
 * Copyright (c) [2024] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

// 通话状态枚举
export const CALL_STATES = {
  IDLE: "idle", // 空闲
  OUTGOING: "outgoing", // 呼叫中
  INCOMING: "incoming", // 呼入（响铃）
  ACTIVE: "active", // 通话中
  ENDED: "ended", // 已结束
};

// 联系人状态枚举
export const CONTACT_STATES = {
  ONLINE: "online",
  OFFLINE: "offline",
  BUSY: "busy", // 正在通话
  AWAY: "away", // 离开
};

// 通话方向
export const CALL_DIRECTIONS = {
  INCOMING: "incoming",
  OUTGOING: "outgoing",
};

// 通话历史状态
export const CALL_HISTORY_STATUS = {
  COMPLETED: "completed",
  MISSED: "missed",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
};

// Action Types
const INITIATE_CALL = "jangouts/call/INITIATE_CALL";
const ACCEPT_CALL = "jangouts/call/ACCEPT_CALL";
const REJECT_CALL = "jangouts/call/REJECT_CALL";
const END_CALL = "jangouts/call/END_CALL";
const CALL_RINGING = "jangouts/call/RINGING";
const CALL_ACTIVE = "jangouts/call/ACTIVE";
const CALL_FAILED = "jangouts/call/FAILED";
const UPDATE_CONTACT_STATUS = "jangouts/call/UPDATE_CONTACT_STATUS";
const ADD_CONTACT = "jangouts/call/ADD_CONTACT";
const REMOVE_CONTACT = "jangouts/call/REMOVE_CONTACT";
const ADD_CALL_HISTORY = "jangouts/call/ADD_HISTORY";
const SET_CALL_DURATION = "jangouts/call/SET_DURATION";
const RECEIVE_INCOMING_CALL = "jangouts/call/RECEIVE_INCOMING_CALL";
const SET_CONTACTS = "jangouts/call/SET_CONTACTS";

// Action Creators
export const actionCreators = {
  // 发起呼叫
  initiateCall: (
    remoteUserId,
    remoteUsername,
    callId,
    roomId,
    isInitiator,
  ) => ({
    type: INITIATE_CALL,
    payload: { remoteUserId, remoteUsername, callId, roomId, isInitiator },
  }),

  // 接受呼叫
  acceptCall: (callId) => ({
    type: ACCEPT_CALL,
    payload: { callId },
  }),

  // 拒绝呼叫
  rejectCall: (callId) => ({
    type: REJECT_CALL,
    payload: { callId },
  }),

  // 结束通话
  endCall: (callId) => ({
    type: END_CALL,
    payload: { callId },
  }),

  // 呼叫响铃（对方正在响铃）
  callRinging: (callId) => ({
    type: CALL_RINGING,
    payload: { callId },
  }),

  // 通话激活（对方接听）
  callActive: (callId) => ({
    type: CALL_ACTIVE,
    payload: { callId },
  }),

  // 呼叫失败
  callFailed: (callId, reason) => ({
    type: CALL_FAILED,
    payload: { callId, reason },
  }),

  // 更新联系人状态
  updateContactStatus: (userId, status) => ({
    type: UPDATE_CONTACT_STATUS,
    payload: { userId, status },
  }),

  // 添加联系人
  addContact: (contact) => ({
    type: ADD_CONTACT,
    payload: { contact },
  }),

  // 移除联系人
  removeContact: (userId) => ({
    type: REMOVE_CONTACT,
    payload: { userId },
  }),

  // 添加通话历史记录
  addCallHistory: (historyItem) => ({
    type: ADD_CALL_HISTORY,
    payload: { historyItem },
  }),

  // 设置通话时长
  setCallDuration: (callId, duration) => ({
    type: SET_CALL_DURATION,
    payload: { callId, duration },
  }),

  // 接收来电
  receiveIncomingCall: (callId, callerId, callerName, roomId, isInitiator) => ({
    type: RECEIVE_INCOMING_CALL,
    payload: { callId, callerId, callerName, roomId, isInitiator },
  }),

  // 设置联系人列表（从API获取）
  setContacts: (contacts) => ({
    type: SET_CONTACTS,
    payload: { contacts },
  }),
};

// Initial State
export const initialState = {
  // 当前通话状态
  currentCall: {
    state: CALL_STATES.IDLE,
    callId: null,
    remoteUserId: null,
    remoteUsername: null,
    startTime: null,
    isInitiator: false,
    error: null,
  },

  // 联系人列表
  contacts: {
    // 示例联系人
    // 'user1': {
    //   id: 'user1',
    //   name: '张三',
    //   status: CONTACT_STATES.ONLINE,
    //   lastSeen: new Date().toISOString()
    // },
    // 'user2': {
    //   id: 'user2',
    //   name: '李四',
    //   status: CONTACT_STATES.ONLINE,
    //   lastSeen: new Date().toISOString()
    // },
    // 'user3': {
    //   id: 'user3',
    //   name: '王五',
    //   status: CONTACT_STATES.ONLINE,
    //   lastSeen: new Date().toISOString()
    // }
  },

  // 呼叫历史
  callHistory: [
    {
      id: "call1",
      remoteUserId: "user1",
      remoteUsername: "张三",
      direction: CALL_DIRECTIONS.OUTGOING,
      duration: 120,
      timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      status: CALL_HISTORY_STATUS.COMPLETED,
    },
    {
      id: "call2",
      remoteUserId: "user2",
      remoteUsername: "李四",
      direction: CALL_DIRECTIONS.INCOMING,
      duration: 0,
      timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      status: CALL_HISTORY_STATUS.MISSED,
    },
  ],
};

// Selectors
export const selectors = {
  // 获取当前通话状态
  getCurrentCall: (state) => state.call.currentCall,

  // 获取联系人列表
  getContacts: (state) => Object.values(state.call.contacts),

  // 获取特定联系人
  getContact: (state, userId) => state.call.contacts[userId],

  // 获取在线联系人
  getOnlineContacts: (state) =>
    Object.values(state.call.contacts).filter(
      (c) => c.status === CONTACT_STATES.ONLINE,
    ),

  // 获取通话历史
  getCallHistory: (state) => state.call.callHistory,

  // 检查是否在通话中
  isInCall: (state) => state.call.currentCall.state === CALL_STATES.ACTIVE,

  // 检查是否有来电
  hasIncomingCall: (state) => state.call.currentCall.state === CALL_STATES.INCOMING,

  // 检查是否在呼叫中
  isOutgoingCall: (state) => state.call.currentCall.state === CALL_STATES.OUTGOING,
};

// Reducer
const reducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case INITIATE_CALL: {
      const { remoteUserId, remoteUsername, callId, roomId, isInitiator } =
        payload;
      console.log(
        "Initiating call=============",
        remoteUserId,
        remoteUsername,
        callId,
        roomId,
        isInitiator,
      );

      return {
        ...state,
        currentCall: {
          state: CALL_STATES.OUTGOING,
          callId,
          remoteUserId,
          remoteUsername,
          startTime: null,
          isInitiator,
          roomId,
          error: null,
        },
      };
    }

    case RECEIVE_INCOMING_CALL: {
      const { callId, callerId, callerName, roomId, isInitiator } = payload;
      return {
        ...state,
        currentCall: {
          state: CALL_STATES.INCOMING,
          callId,
          remoteUserId: String(callerId),
          remoteUsername: callerName,
          startTime: null,
          isInitiator,
          roomId,
          error: null,
        },
      };
    }

    case CALL_RINGING: {
      const { callId } = payload;
      if (state.currentCall.callId !== callId) return state;

      return {
        ...state,
        currentCall: {
          ...state.currentCall,
          state: CALL_STATES.INCOMING,
        },
      };
    }

    case CALL_ACTIVE: {
      const { callId } = payload;
      if (state.currentCall.callId !== callId) return state;

      return {
        ...state,
        currentCall: {
          ...state.currentCall,
          state: CALL_STATES.ACTIVE,
          startTime: new Date().toISOString(),
        },
        contacts: {
          ...state.contacts,
          [state.currentCall.remoteUserId]: {
            ...state.contacts[state.currentCall.remoteUserId],
            status: CONTACT_STATES.BUSY,
          },
        },
      };
    }

    case ACCEPT_CALL: {
      const { callId } = payload;
      if (state.currentCall.callId !== callId) return state;

      return {
        ...state,
        currentCall: {
          ...state.currentCall,
          state: CALL_STATES.ACTIVE,
          startTime: new Date().toISOString(),
        },
      };
    }

    case END_CALL: {
      const { callId } = payload;
      if (state.currentCall.callId !== callId) return state;

      return {
        ...state,
        currentCall: {
          ...initialState.currentCall,
        },
        contacts: {
          ...state.contacts,
          [state.currentCall.remoteUserId]: {
            ...state.contacts[state.currentCall.remoteUserId],
            status: CONTACT_STATES.ONLINE,
          },
        },
      };
    }

    case REJECT_CALL: {
      const { callId } = payload;
      if (state.currentCall.callId !== callId) return state;

      

      return {
        ...state,
        currentCall: {
          ...initialState.currentCall,
        }
      };
    }

    case CALL_FAILED: {
      const { callId, reason } = payload;
      if (state.currentCall.callId !== callId) return state;

      const historyItem = {
        id: `call_${Date.now()}`,
        remoteUserId: state.currentCall.remoteUserId,
        remoteUsername: state.currentCall.remoteUsername,
        direction: state.currentCall.isInitiator
          ? CALL_DIRECTIONS.OUTGOING
          : CALL_DIRECTIONS.INCOMING,
        duration: 0,
        timestamp: new Date().toISOString(),
        status: CALL_HISTORY_STATUS.CANCELLED,
      };

      return {
        ...state,
        currentCall: {
          ...initialState.currentCall,
          error: reason,
        },
        callHistory: [historyItem, ...state.callHistory.slice(0, 49)],
      };
    }

    case UPDATE_CONTACT_STATUS: {
      const { userId, status } = payload;
      if (!state.contacts[userId]) return state;

      return {
        ...state,
        contacts: {
          ...state.contacts,
          [userId]: {
            ...state.contacts[userId],
            status,
            lastSeen: new Date().toISOString(),
          },
        },
      };
    }

    case ADD_CONTACT: {
      const { contact } = payload;
      return {
        ...state,
        contacts: {
          ...state.contacts,
          [contact.id]: {
            ...contact,
            lastSeen: contact.lastSeen || new Date().toISOString(),
          },
        },
      };
    }

    case REMOVE_CONTACT: {
      const { userId } = payload;
      const newContacts = { ...state.contacts };
      delete newContacts[userId];

      return {
        ...state,
        contacts: newContacts,
      };
    }

    case ADD_CALL_HISTORY: {
      const { historyItem } = payload;
      return {
        ...state,
        callHistory: [historyItem, ...state.callHistory.slice(0, 49)],
      };
    }

    case SET_CALL_DURATION: {
      const { callId, duration } = payload;
      // 找到对应的通话历史记录并更新时长
      const updatedHistory = state.callHistory.map((item) =>
        item.id === callId ? { ...item, duration } : item,
      );

      return {
        ...state,
        callHistory: updatedHistory,
      };
    }

    case SET_CONTACTS: {
      const { contacts } = payload;
      // 将联系人数组转换为对象，key 为 id
      const contactsObject = contacts.reduce((acc, contact) => {
        acc[contact.id] = {
          ...contact,
          lastSeen: contact.lastSeen || new Date().toISOString(),
        };
        return acc;
      }, {});

      return {
        ...state,
        contacts: contactsObject,
      };
    }

    default:
      return state;
  }
};

export default reducer;
